import React from 'react';
import { useSlidingPill } from '../../hooks/useSlidingPill';
import { Text } from '@ui-library';

export type CABPanelTab = 'config' | 'chat';

interface CABPanelTabsProps {
  activeTab: CABPanelTab;
  onTabChange: (tab: CABPanelTab) => void;
  disabled?: boolean;
}

export const CABPanelTabs: React.FC<CABPanelTabsProps> = ({
  activeTab,
  onTabChange,
  disabled = false,
}) => {
  const tabs = [
    {
      id: 'config' as CABPanelTab,
      label: 'CAB Configuration',
    },
    {
      id: 'chat' as CABPanelTab,
      label: 'Veylo CAB Agent',
    },
  ];

  const { containerRef, registerTab, rect, animate } = useSlidingPill(activeTab);

  return (
    /* Outer row keeps the 56px height so this panel's header stays aligned with
       the preview panel's; the pill border lives on the inner group. */
    <div className="flex h-[56px] items-center px-bg-v2-xl bg-bg-primary">
      <div
        ref={containerRef}
        className="relative flex w-full items-center gap-bg-v2-xs p-bg-v2-xs bg-bg-primary border border-border-primary rounded-bg-v2-full shadow-bg-v2-xs"
        role="tablist"
      >
        {rect && (
          <span
            aria-hidden="true"
            className={`absolute rounded-bg-v2-full bg-bg-quaternary pointer-events-none ${
              animate ? 'transition-all duration-300 ease-out motion-reduce:transition-none' : ''
            }`}
            style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          />
        )}
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            ref={registerTab(tab.id)}
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${tab.id}`}
            disabled={disabled}
            onClick={() => onTabChange(tab.id)}
            // No disabled:opacity — colour stays full when disabled; the
            // not-allowed cursor is signal enough.
            className={`
              relative z-10
              flex-1 py-bg-v2-sm px-bg-v2-lg rounded-bg-v2-full transition-colors
              flex items-center justify-center gap-bg-v2-sm
              disabled:cursor-not-allowed
              ${isActive ? 'bg-transparent' : 'bg-transparent hover:bg-bg-secondary'}
            `}
          >
            <Text
              variant="text-sm"
              weight="semibold"
              className={isActive ? '!text-text-primary' : '!text-text-tertiary'}
            >
              {tab.label}
            </Text>
          </button>
        );
      })}
      </div>
    </div>
  );
};
